import React from 'react';

interface BarGroup {
  label: string;
  values: number[];
}

interface BarChartProps {
  data: BarGroup[];
  colors?: string[];
  legend?: string[];
  height?: number;
  barWidth?: number;
}

const DEFAULT_COLORS = ['#8B6F47', '#B8860B', '#7A8C6E', '#C4956A', '#8B7355', '#D4C4A8'];

export default function BarChart({
  data,
  colors = DEFAULT_COLORS,
  legend,
  height = 200,
  barWidth = 32,
}: BarChartProps) {
  if (!data.length) {
    return <div className="empty-state" style={{ padding: 40 }}>Keine Daten vorhanden</div>;
  }

  const seriesCount = data[0]?.values.length || 1;
  const gap = 4;
  const groupGap = 16;
  const groupWidth = seriesCount * (barWidth + gap) - gap;
  const chartWidth = data.length * (groupWidth + groupGap) - groupGap + 60;
  const maxVal = Math.max(...data.flatMap((d) => d.values), 1);

  const fmtShort = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toFixed(0);
  };

  return (
    <div>
      {legend && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          {legend.map((l, i) => (
            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: colors[i % colors.length], display: 'inline-block' }} />
              {l}
            </span>
          ))}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <svg width={chartWidth} height={height + 40} style={{ display: 'block' }}>
          {data.map((group, gi) => {
            const gx = 40 + gi * (groupWidth + groupGap);
            return (
              <g key={group.label}>
                {group.values.map((val, vi) => {
                  const barH = maxVal > 0 ? (val / maxVal) * height : 0;
                  const x = gx + vi * (barWidth + gap);
                  const y = height - barH;
                  return (
                    <g key={vi}>
                      <rect
                        x={x} y={y} width={barWidth} height={barH}
                        rx={4}
                        fill={val > 0 ? colors[vi % colors.length] : 'var(--border)'}
                        opacity={val > 0 ? 0.85 : 0.3}
                      />
                      {val > 0 && (
                        <text x={x + barWidth / 2} y={y - 5} textAnchor="middle" fontSize="9" fill={colors[vi % colors.length]} fontWeight="600">
                          {fmtShort(val)}
                        </text>
                      )}
                    </g>
                  );
                })}
                <text x={gx + groupWidth / 2} y={height + 16} textAnchor="middle" fontSize="10" fill="var(--text-muted)">
                  {group.label}
                </text>
              </g>
            );
          })}
          <line x1="38" y1={height} x2={chartWidth} y2={height} stroke="var(--border)" strokeWidth="1" />
        </svg>
      </div>
    </div>
  );
}
